import { type ReactNode, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Link2, StickyNote, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { PRESS_NONE } from "@/components/motion";
import { KoryIcon } from "@/components/kory/kory-icon";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { MentionTextarea } from "@/components/mentions/mention-textarea";
import { COMPOSER_BUTTON, COMPOSER_FIELD, COMPOSER_LEAD_SLOT } from "@/components/todos/todo-composer";
import { MentionText } from "@/components/mentions/mention-text";
import { pruneMentionIds, toMentionCandidates } from "@/components/mentions/mentions";
import { usePeoplePool } from "@/components/people/person-picker";
import { useAuth } from "@/contexts/auth-context";
import { useCapabilities } from "@/hooks/use-capabilities";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  useAddLeadNote,
  useDeleteLeadNote,
  useLeadNotes,
} from "./hooks/useLeads";
import { LeadCreatePdEntityDialog } from "./LeadCreatePdEntityDialog";
import { usePipedriveFrozen } from "@/hooks/use-brands";
import { TAP_AREA } from "@/lib/tap-area";
import { cn } from "@/lib/utils";
import type { LeadNote } from "./types";

interface LeadNotesProps {
  leadId: string;
  // Optional — enables the Pipedrive sync + create-prompt (see LeadNotesContent).
  // Production callers always pass it; omitting it renders a plain notes thread.
  tenantId?: string;
  notesCount: number;
  /** Additive: fires after add/delete settles, alongside the hooks' own
   * `["lead-notes", leadId]`/`["leads"]` invalidation — see
   * LeadQualifyDialogProps.onSettled for the CCC drill-in's use. */
  onSettled?: () => void;
}

/**
 * One note as the thread renders it — author, provenance badge, relative time,
 * the body, and the delete for its author or a triager. Exported so the deal
 * timeline can interleave notes with the deal's other events using the same
 * markup and the same delete path.
 */
export function LeadNoteItem({
  note,
  leadId,
  onSettled,
}: {
  note: LeadNote;
  leadId: string;
  onSettled?: () => void;
}) {
  const { user } = useAuth();
  const { can } = useCapabilities();
  const canModerate = can("lead_management.triage");
  const deleteNote = useDeleteLeadNote();

  const canDelete = !!user && (note.author_id === user.id || canModerate);
  const isPipedrive = note.source_system === "pipedrive";
  // The daily deal plan's analysis note (``sop.hickory_deal.daily_plan``): Kory's leaf is the
  // attribution, so the author line is left to the badge.
  const isKory = note.source_system === "kory_daily_plan";
  // The badge carries provenance, so strip the importer's "Pipedrive"
  // author prefix and show just the rep. A name-less import (author_name
  // is bare "Pipedrive" or null) collapses to null → badge + timestamp
  // only; the badge is the attribution, so no "Unknown" fallback here.
  const displayAuthor = isPipedrive
    ? note.author_name?.replace(/^Pipedrive(?: · )?/, "").trim() || null
    : isKory
      ? null
      : (note.author_name ?? "Unknown");
  return (
    <div data-testid="lead-note-item" className="rounded-sm bg-muted/30 px-2 py-1.5 text-xs">
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-x-1 text-[11px] text-muted-foreground">
          {isPipedrive && (
            <Badge
              variant="secondary"
              data-testid="lead-note-pipedrive-badge"
              className="rounded-full px-1.5 py-0 text-[10px] font-medium bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300"
            >
              <Link2 className="mr-0.5 h-2.5 w-2.5" />
              Pipedrive
            </Badge>
          )}
          {isKory && (
            <Badge
              variant="secondary"
              data-testid="lead-note-kory-badge"
              title="Written by Kory"
              className="rounded-full px-1.5 py-0 text-[10px] font-medium"
            >
              <KoryIcon alt="" className="mr-0.5 h-2.5 w-2.5" />
              Kory
            </Badge>
          )}
          {displayAuthor && (
            <span className="font-medium text-foreground">
              {displayAuthor}
            </span>
          )}
          {displayAuthor && <span>·</span>}
          <span>
            {formatDistanceToNow(new Date(note.created_at), {
              addSuffix: true,
            })}
          </span>
        </div>
        {canDelete && (
          <button
            type="button"
            data-testid="lead-note-delete"
            aria-label="Delete note"
            className="text-muted-foreground hover:text-destructive transition-colors"
            disabled={deleteNote.isPending}
            onClick={() => deleteNote.mutate({ leadId, noteId: note.id }, { onSettled })}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <div className="whitespace-pre-wrap break-words text-foreground">
        <MentionText text={note.body} names={(note.mentions ?? []).map((m) => m.display_name)} />
      </div>
    </div>
  );
}

/**
 * The composer on its own — textarea, Add note, and the Pipedrive behaviour a
 * saved note carries (sync toast; create-PD-entity prompt on a Sales lead with
 * no PD record). Exported so the deal timeline can put it above a merged feed;
 * the write path is the same `useAddLeadNote` either way.
 */
export function LeadNoteComposer({
  leadId,
  tenantId,
  onSettled,
  mentionSuggestionsPlacement = "above",
  compact = false,
  trailing,
}: {
  leadId: string;
  tenantId?: string;
  onSettled?: () => void;
  /** Open the @-mention list below when the composer tops its feed. */
  mentionSuggestionsPlacement?: "above" | "below";
  /** One line at rest, growing on focus or once there is text; "Add note" moves inline beside
   *  the box and shows only once text is typed, instead of its own row below (the deal
   *  Timeline's compact header). The row takes the to-do list's geometry (`rounded-md border
   *  px-2.5 py-1.5`, a 20px leading icon slot, a 32px input line), so it lines up with the Next
   *  steps list above it: icon column, text start, right edge and height. */
  compact?: boolean;
  /** Rendered at the end of the same row as the box in `compact` mode — the Timeline's
   *  "Log a contact" icon button. Ignored outside `compact`. */
  trailing?: ReactNode;
}) {
  const [draft, setDraft] = useState("");
  const [pickedMentionIds, setPickedMentionIds] = useState<Set<string>>(new Set());
  // Fetch the mention pool on first focus, not on mount — a board renders
  // many composers and most never get typed in.
  const [mentionsWanted, setMentionsWanted] = useState(false);
  const mentionsEnabled = mentionsWanted && !!tenantId;
  const { data: mentionPool } = usePeoplePool("deal_viewer", tenantId, { enabled: mentionsEnabled });
  const mentionCandidates = useMemo(() => toMentionCandidates(mentionPool ?? []), [mentionPool]);
  const [pdDialogOpen, setPdDialogOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const isMobile = useIsMobile();
  const addNote = useAddLeadNote();
  // Only a brand KNOWN to still be on Pipedrive gets the create prompt. A brand
  // that has left it never does (the backend already answers `disabled`; this
  // guard keeps the prompt from ever mounting), and neither does a brand whose
  // fact has not loaded yet.
  const pipedriveLive = usePipedriveFrozen(tenantId) === false;

  const handleAdd = async () => {
    const trimmed = draft.trim();
    if (!trimmed || addNote.isPending) return;
    try {
      const created = await addNote.mutateAsync({
        leadId,
        body: trimmed,
        mentionedUserIds: pruneMentionIds(trimmed, pickedMentionIds, mentionCandidates),
      });
      setDraft("");
      setPickedMentionIds(new Set());
      const pd = created.pd_sync;
      if (pd?.action === "enqueued") {
        toast.success(`Note synced to the Pipedrive ${pd.target ?? "record"}.`);
      } else if (pd?.suggest_create && tenantId && pipedriveLive) {
        setPdDialogOpen(true);
      }
    } catch {
      // useAddLeadNote already surfaces a toast on error.
    } finally {
      onSettled?.();
    }
  };

  const grown = focused || draft.trim().length > 0;
  const submitButton = (
    <Button
      type="button"
      size="sm"
      data-testid="lead-note-submit"
      className={compact ? cn(COMPOSER_BUTTON, "relative", TAP_AREA) : undefined}
      disabled={!draft.trim() || addNote.isPending}
      onClick={handleAdd}
      aria-busy={addNote.isPending}
    >
      {addNote.isPending ? "Adding…" : "Add Note"}
    </Button>
  );

  return (
    <div
      className={
        compact
          ? // The to-do composer's row in a border. The input drops its own border and ring, so the frame shows focus.
            cn("flex items-start gap-x-3 focus-within:border-ring", COMPOSER_FIELD)
          : "flex flex-col gap-1.5 pt-1 text-xs"
      }
      data-testid="lead-note-composer"
    >
      {compact ? (
        // The slot spans the 32px input line, since this row aligns to the top as it grows.
        <span className={cn(COMPOSER_LEAD_SLOT, "h-8")} aria-hidden>
          <StickyNote className="size-4" />
        </span>
      ) : null}
      <div className="min-w-0 flex-1">
        <MentionTextarea
          data-testid="lead-note-input"
          // The compact row's hint is shorter, to fit beside its trailing button, and drops on a phone.
          placeholder={
            !tenantId || (compact && isMobile)
              ? "Add a note…"
              : compact
                ? "Add a note… @ to mention"
                : "Add a note… Use @ to mention a teammate."
          }
          value={draft}
          maxLength={4000}
          disabled={addNote.isPending}
          onChange={setDraft}
          onFocus={() => {
            setMentionsWanted(true);
            setFocused(true);
          }}
          onBlur={() => setFocused(false)}
          pool="deal_viewer"
          tenantId={tenantId}
          enabled={mentionsEnabled}
          onMentionPick={(c) => setPickedMentionIds((prev) => new Set(prev).add(c.id))}
          suggestionsPlacement={mentionSuggestionsPlacement}
          rows={compact && !grown ? 1 : 2}
          className={
            compact
              ? cn(
                  "min-h-8 resize-none border-0 bg-transparent px-0 py-1.5 text-base shadow-none focus-visible:ring-0 md:text-sm dark:bg-transparent",
                  grown ? "min-h-14" : "h-8",
                )
              : "min-h-[2.5rem] bg-card text-base md:text-xs"
          }
        />
      </div>
      {compact ? (
        // Below `md` a trailing icon's tap area grows to 44px (`TAP_AREA`): 16px apart keeps it off "Add note".
        <div className="flex h-8 shrink-0 items-center gap-1 max-md:gap-4">
          {draft.trim() ? submitButton : null}
          {trailing}
        </div>
      ) : (
        <div className="flex justify-end">{submitButton}</div>
      )}

      {tenantId && pipedriveLive && (
        <LeadCreatePdEntityDialog
          leadId={leadId}
          tenantId={tenantId}
          open={pdDialogOpen}
          onOpenChange={setPdDialogOpen}
          onSettled={onSettled}
        />
      )}
    </div>
  );
}

/**
 * The notes list + composer, without the collapsible chrome — mounted only
 * while visible, so the query fires on mount. The LeadCard intel rail embeds
 * this directly; `LeadNotes` wraps it in a collapsible for the detail sheet.
 *
 * `tenantId` enables the Pipedrive integration: a note saved on a linked lead
 * syncs to PD (a low-key toast), and a Sales lead with no PD entity prompts to
 * create one. Omit it to render a plain notes thread with no PD behavior.
 */
export function LeadNotesContent({
  leadId,
  tenantId,
  onSettled,
}: {
  leadId: string;
  tenantId?: string;
  onSettled?: () => void;
}) {
  const notesQuery = useLeadNotes(leadId, { enabled: true });
  const notes = notesQuery.data ?? [];

  return (
    <div className="space-y-2 text-xs" data-testid="lead-notes-body">
      {notesQuery.isLoading && (
        <div className="text-muted-foreground italic">Loading…</div>
      )}
      {!notesQuery.isLoading && notes.length === 0 && (
        <div className="text-muted-foreground italic">No notes yet.</div>
      )}
      {notes.map((note) => (
        <LeadNoteItem key={note.id} note={note} leadId={leadId} onSettled={onSettled} />
      ))}

      <LeadNoteComposer leadId={leadId} tenantId={tenantId} onSettled={onSettled} />
    </div>
  );
}

export function LeadNotes({ leadId, tenantId, notesCount, onSettled }: LeadNotesProps) {
  const [open, setOpen] = useState(false);

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="mt-2 rounded-md border border-border/60"
    >
      {/* Full width: no press scale inside a press-scoped tray (the contact tray), or an edge press misses. */}
      <CollapsibleTrigger
        data-testid="lead-notes-trigger"
        {...PRESS_NONE}
        className="flex w-full items-center gap-1.5 px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-muted/50 transition-colors"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5" />
        )}
        <span className="font-medium">Notes ({notesCount})</span>
      </CollapsibleTrigger>
      <CollapsibleContent
        data-testid="lead-notes-content"
        className="px-3 pb-3 pt-1"
      >
        <LeadNotesContent leadId={leadId} tenantId={tenantId} onSettled={onSettled} />
      </CollapsibleContent>
    </Collapsible>
  );
}
